/** E13.4 Betriebsübersicht des Anbieters: je Laden Kette, Jobs, Abo; Cron-Läufe; Fehlerliste; E13.5 Schlüsselwechsel. */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, Notice, Seitenzustand, StatusBadge } from "../ui";
import { Card, Dl, PageHeader } from "../ui/Layout";
import { api, rufe } from "../api/client";
import { formatDatumZeit, formatTag } from "@shared/zeit";
import { aboStatusText, type AboStatus } from "@shared/schemas/anbieter";

interface Betrieb {
  umgebung: string;
  stripe: boolean;
  backups: string;
  crons: { name: string; lauf: { at: string; wert: number } | null }[];
  fehler: { at: string; art?: string; ladenId?: string | null; fehler?: string; grund?: string }[];
  laeden: {
    id: string;
    name: string;
    slug: string;
    status: string;
    regale: number;
    mieter: number;
    sumupVerbundenAt: string | null;
    supportLetzterAt: string | null;
    abo: { aboStatus: AboStatus; testphaseBis: string | null; gesperrt: boolean };
    kette: { at: string; wert: { ok: boolean; anzahl?: number; nr?: number; grund?: string } } | null;
    jobs: { at: string; wert: Record<string, number> } | null;
  }[];
}

const CRON_NAMEN: Record<string, string> = { "5min": "Aufräumen (alle 5 Minuten)", taeglich: "Nachtlauf 03:00 (Kette, Buchungen, Erinnerungen, Mahnungen, Abo)", abend: "Tagesbilanz 17:00", woche: "Wochenmail Sonntag 16:00" };

export function Betrieb() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["admin", "betrieb"], queryFn: () => rufe<Betrieb>(api.api.admin.betrieb.$get()), refetchInterval: 60_000 });
  const wechsel = useMutation({ mutationFn: () => rufe<{ mieterIban: number; ladenBank: number; sumupTokens: number; totp: number }>(api.api.admin.schluesselwechsel.$post()), onSuccess: () => void qc.invalidateQueries({ queryKey: ["admin"] }) });
  return (
    <>
      <PageHeader title="Betrieb" sub="Protokoll-Ketten, Hintergrundläufe, Fehler und Schlüssel" />
      <Seitenzustand query={q}>
        {(d) => (
          <div className="sc-col" style={{ gap: "var(--space-4)" }}>
            <Card title="Umgebung">
              <Dl rows={[["Umgebung", d.umgebung], ["Stripe", d.stripe ? <StatusBadge status="paid">eingerichtet</StatusBadge> : <StatusBadge status="neutral">nicht eingerichtet (Secrets STRIPE_SECRET_KEY, STRIPE_WEBHOOK_SECRET, STRIPE_PRICE_ID)</StatusBadge>], ["Sicherung", d.backups]]} />
            </Card>
            <Card title="Hintergrundläufe">
              <Dl rows={d.crons.map((c) => [CRON_NAMEN[c.name] ?? c.name, c.lauf ? `zuletzt ${formatDatumZeit(c.lauf.at)}` : "noch kein Lauf notiert"])} />
            </Card>
            <Card title="Läden" flush>
              <div className="sc-scroll">
                <table className="rg-table">
                  <thead>
                    <tr>
                      <th>Laden</th>
                      <th>Abo</th>
                      <th>Kette</th>
                      <th>Jobs (30 Tage)</th>
                      <th>Support</th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.laeden.map((l) => (
                      <tr key={l.id}>
                        <td>
                          <span className="sc-strong">{l.name}</span>
                          <div className="sc-muted sc-small">{l.regale} Regale · {l.mieter} Mieter · SumUp {l.sumupVerbundenAt ? "verbunden" : "offen"}</div>
                        </td>
                        <td>
                          <StatusBadge status={l.abo.gesperrt ? "error" : l.abo.aboStatus === "test" ? "wait" : "paid"}>{aboStatusText(l.abo.aboStatus)}</StatusBadge>
                          {l.abo.testphaseBis ? <div className="sc-muted sc-small">bis {formatTag(l.abo.testphaseBis)}</div> : null}
                        </td>
                        <td>{l.kette ? <><StatusBadge status={l.kette.wert.ok ? "paid" : "error"}>{l.kette.wert.ok ? `in Ordnung (${l.kette.wert.anzahl ?? "?"})` : `Bruch bei Nr. ${l.kette.wert.nr}`}</StatusBadge><div className="sc-muted sc-small">{formatDatumZeit(l.kette.at)}</div></> : <span className="sc-muted">noch nicht geprüft</span>}</td>
                        <td className="sc-small">{l.jobs ? Object.entries(l.jobs.wert).map(([k, v]) => `${k} ${v}`).join(" · ") : <span className="sc-muted">keine</span>}</td>
                        <td className="sc-small">{l.supportLetzterAt ? formatDatumZeit(l.supportLetzterAt) : <span className="sc-muted">nie</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
            <Card title="Fehler (letzte 50)">
              {d.fehler.length === 0 ? <p className="sc-muted">Keine Fehler notiert.</p> : (
                <ul className="sc-col sc-small" style={{ gap: "var(--space-2)", paddingLeft: "var(--space-4)" }}>
                  {d.fehler.map((f, i) => (
                    <li key={i}><span className="sc-muted">{formatDatumZeit(f.at)}</span> · {f.art ?? "?"} {f.ladenId ? `· Laden ${f.ladenId.slice(0, 8)}` : ""} · {f.fehler ?? f.grund ?? ""}</li>
                  ))}
                </ul>
              )}
            </Card>
            <Card title="Schlüsselwechsel">
              <p className="sc-muted">Ablauf: neuen Schlüssel erzeugen, als Secret VERSCHLUESSELUNG_KEY setzen und den bisherigen als VERSCHLUESSELUNG_KEY_ALT. Dann hier umschlüsseln (IBANs, Bankverbindungen, SumUp-Tokens, TOTP-Geheimnisse). Danach das alte Secret löschen. Einzelheiten in docs/BETRIEB.md.</p>
              <div className="sc-toolbar">
                <Button variant="secondary" icon="shield-check" onClick={() => { if (window.confirm("Jetzt alle verschlüsselten Felder mit dem aktuellen Schlüssel neu schreiben?")) wechsel.mutate(); }} loading={wechsel.isPending}>
                  Umschlüsseln
                </Button>
              </div>
              {wechsel.isSuccess ? <Notice tone="success">Umgeschlüsselt: {wechsel.data.mieterIban} IBANs, {wechsel.data.ladenBank} Bankverbindungen, {wechsel.data.sumupTokens} SumUp-Tokens, {wechsel.data.totp} TOTP-Geheimnisse.</Notice> : null}
              {wechsel.isError ? <Notice tone="error">{wechsel.error.message}</Notice> : null}
            </Card>
          </div>
        )}
      </Seitenzustand>
    </>
  );
}
